import axios from "axios";
import {
  Action,
  getModule,
  Module,
  Mutation,
  VuexModule,
} from "vuex-module-decorators";
import { Vue } from "vue-property-decorator";
import APP_UTILITIES from "@/utilities/commonFunctions";
import { ApiErrorResponse, ApiErrorType } from "@/Model/error/api/types";
import store from "@/store";
import {
  AddCartItemActionPayload,
  AddCartItemRequest,
  Cart,
  CartItem,
  CustomFeesTotal,
  GetCartResponse,
  UpdateCartItemActionPayload,
  GetCartApplicationsResponse,
  CartApplication,
  IndexAction,
  RemoveCartItemRequest,
  StorePolicy,
  AssociateScholarToCartItemRequest,
  LoadCartApplicationsDataConfig,
  PromoCodeValidationResponse,
  SetCartPayload,
  CustomCheckoutOption,
  PaymentOptions,
  PaymentPlans,
} from "@/Model/checkout/cart/types";
import MutationNames from "@/store/modules/checkout/cart/mutationNames";
import {
  addNewItem,
  getCartApplications,
  getCartForUser,
  updateItem,
  removeCartItem,
  getTermsOfService,
  associateScholar,
  getCustomCheckoutOptionList,
} from "@/services/checkout/cart/api";
import commonEntryStore from "@/store/modules/commonEntry";
import { ScreenText } from "@/lang/ScreenText";
import ToastType from "@/Model/toastTypes";
import applicationStore from "@/store/modules/applications/applicationStore";
import formsStore from "@/store/modules/forms/formsStore";
import {
  CatalogSession,
  SessionEnrollmentStatus,
} from "@/Model/sessions/catalogTypes";
import APP_CONST from "@/constants/AppConst";
import CartLocalStorageUtilities from "@/utilities/cartStorage";
import { ScholarDropdown } from "@/Model/model";
import { IApplicationData } from "@/Model/programModel";
import { hasWaitlistIsUnlimitedActive } from "@/utilities/session";

const {
  FETCH_CART_REQUESTED,
  FETCH_CART_SUCCEEDED,
  FETCH_CART_FAILED,
  RESET_CART,
  ADD_CARTITEM_REQUESTED,
  ADD_CARTITEM_SUCCEEDED,
  ADD_CARTITEM_FAILED,
  UPDATE_CARTITEM_REQUESTED,
  UPDATE_CARTITEM_SUCCEEDED,
  UPDATE_CARTITEM_FAILED,
  FETCH_CART_APPLICATIONS_REQUESTED,
  FETCH_CART_APPLICATIONS_SUCCEEDED,
  FETCH_CART_APPLICATIONS_FAILED,
  RESET_CART_APPLICATIONS,
  SET_IS_LOADING_CART_APPLICATIONS,
  INCREMENT_CURRENTLY_SELECTED_APPLICATION,
  DECREMENT_CURRENTLY_SELECTED_APPLICATION,
  APPLICATIONS_COMPLETE,
  RESET_APPLICATIONS_COMPLETE,
  DELETE_CARTITEM_REQUESTED,
  DELETE_CARTITEM_SUCCEEDED,
  DELETE_CARTITEM_FAILED,
  IS_INITIALIZING_CART_REQUESTED,
  IS_INITIALIZING_CART_COMPLETED,
  SET_CURRENTLY_SELECTED_APPLICATION_INDEX,
  VALIDATE_AND_SET_CART,
  COPY_ACTIVE_CART_TO_SUBMITTED_CART,
  RESET_SUBMITTED_CART,
  FETCH_STORE_POLICY_REQUESTED,
  FETCH_STORE_POLICY_SUCCEEDED,
  FETCH_STORE_POLICY_FAILED,
  RESET_STORE_POLICY,
  ASSOCIATE_SCHOLAR_REQUESTED,
  ASSOCIATE_SCHOLAR_SUCCEEDED,
  ASSOCIATE_SCHOLAR_FAILED,
  SET_PROMO_CODE,
  SET_FETCHING_CART_FOR_PROMO_CODE,
  FETCH_CUSTOM_CHECKOUT_REQUESTED,
  FETCH_CUSTOM_CHECKOUT_SUCCEEDED,
  FETCH_CUSTOM_CHECKOUT_FAILED,
  SET_CUSTOM_CHECKOUT,
  SET_SELECTED_PAYMENT_OPTION,
} = MutationNames;

@Module({
  namespaced: true,
  name: "cart",
  store,
  dynamic: true,
})
export class CartStoreModule extends VuexModule {
  screenText = new ScreenText();
  public APP_ROUTES = APP_CONST.APP_ROUTES;

  // Default custom checkout value, value out of range to avoid issues when using undefined values
  private customCheckoutOption: CustomCheckoutOption = {
    accountID: -1,
    autoEnrollStatusId: -1,
    id: -1,
    instructions: "",
    isEnabled: false,
    name: "",
  };

  // initialize default value for payment option, payment online using stripe is the default option
  private selectedPaymentOption = PaymentOptions.ONLINE_PAYMENT;
  private activeCart: Cart = {
    userId: 0,
    cartId: 0,
    totals: {
      subtotal: 0,
      tax: 0,
      promoCodeDiscount: 0,
      discountPercentage: 0,
      total: 0,
    },
    promoCodeValidation: null,
    cartItems: [],
    paymentPlans: [
      {
        dueNow: {
          amount: 0,
          frequency: 1
        },
        subtitle: '',
        title: ''
      }
    ]
  };
  isFetchingCart = false;
  didFetchCartFail = false;
  fetchCartFailedError: ApiErrorResponse | null = null;

  isFetchingCustomCheckout = false;
  didFetchCustomCheckoutFail = false;
  fetchCustomCheckoutFailedError: ApiErrorResponse | null = null;

  currentInteractionSessionId: number | null = null;
  currentlySelectedApplicationIndex: number = 0;
  applicationsComplete: boolean = false;
  isInitializingCart: boolean = false;
  invalidItemsRemoved: boolean = false;

  isAddingToCart = false;
  didCartAddFail = false;
  cartAddFailedError: ApiErrorResponse | null = null;

  isUpdatingCart = false;
  didCartUpdateFail = false;
  cartUpdateFailedError: ApiErrorResponse | null = null;

  cartApplications: GetCartApplicationsResponse | null = null;
  isFetchingCartApplications: boolean = false;
  didFetchCartApplicationsFail = false;
  fetchCartApplicationsFailedError: ApiErrorResponse | null = null;
  isLoadingCartApplicationsData: boolean = false;

  isDeleting = false;
  didCartDeleteFail = true;
  cartDeleteFailedError: ApiErrorResponse | null = null;

  submittedCart: Cart | null = null;

  isFetchingStorePolicy: boolean = false;
  didFetchStorePolicyFail: boolean = false;
  fetchStorePolicyFailedError: ApiErrorResponse | null = null;
  storePolicy: StorePolicy = {
    enabled: false,
    url: "",
  };
  isFetchingCartForPromoCode: boolean = false;

  isAssociatingScholar: boolean = false;
  associateScholarFailedError: ApiErrorResponse | null = null;

  promoCode: string = "";
  hasPromoCodeError: boolean = false;
  promoCodeErrorMessage: string | null = "";
  isPromoCodeApplied: boolean = false;

  // get selected custom checkout option (if available for the account)

  get customCheckout(): CustomCheckoutOption | undefined {
    return this.customCheckoutOption;
  }

  // get selected payment option between online payment (default option) and custom payment (if custom payment is available)
  get selectedPayment(): PaymentOptions {
    return this.selectedPaymentOption;
  }

  get activeCartId(): number {
    return this.activeCart.cartId;
  }

  get cartItems(): CartItem[] {
    return this.activeCart.cartItems;
  }

  get paymentPlans(): PaymentPlans[] | undefined {
    return this.activeCart.paymentPlans;
  }

  get cartItemsBySession(): { session: CatalogSession; items: CartItem[] }[] {
    const unsorted = Object.values(
      this.cartItems.reduce((map, item) => {
        map[item.session.sessionId] = map[item.session.sessionId] || {
          session: item.session,
          items: [],
        };
        map[item.session.sessionId].items.push(item);
        return map;
      }, {} as { [sessionId: number]: { session: CatalogSession; items: CartItem[] } })
    );

    const sorted = unsorted.sort((a, b) => {
      return a.items[0].cartItemId - b.items[0].cartItemId;
    });

    return sorted;
  }

  get isCartEmpty(): boolean {
    return this.cartItems.length === 0;
  }

  get subtotal(): number {
    if (this.isUserLoggedIn) {
      return this.activeCart.totals.subtotal;
    } else {
      // If user is not logged in, calculate subtotal manually and include discounts in the calculation
      return this.cartItems.reduce((acc, item) => acc + (item.session.price - (item.session.earlyRegistrationDiscount.netItemDiscount + item.session.multiParticipantDiscount.netItemDiscount)), 0);
    }
  }

  get tax(): number | null {
    if (this.isUserLoggedIn) {
      return this.activeCart.totals.tax;
    } else {
      return null;
    }
  }

  get customFeesTotal(): CustomFeesTotal | null {
    if (this.isUserLoggedIn && this.activeCart.totals.customFeesTotal) {
      return this.activeCart.totals.customFeesTotal;
    } else {
      return null;
    }
  }

  get total(): number | null {
    if (this.isUserLoggedIn) {
      return this.activeCart.totals.total;
    } else {
      return null;
    }
  }

  get promoCodeTotal(): number | null {
    if (this.isUserLoggedIn && this.activeCart.totals.promoCodeDiscount) {
      return this.activeCart.totals.promoCodeDiscount;
    } else {
      return null;
    }
  }

  get promoCodeDiscountPercent(): number | null {
    if (this.isUserLoggedIn && this.activeCart.totals.discountPercentage) {
      return this.activeCart.totals.discountPercentage;
    } else {
      return null;
    }
  }

  get promoCodeCartObject(): PromoCodeValidationResponse | null {
    return this.activeCart.promoCodeValidation;
  }

  get isPromoCodeBlockingCheckout(): boolean {
    if (
      (this.isPromoCodeApplied || this.hasPromoCodeError) &&
      this.promoCode &&
      !this.isPromoCodeAppliedSuccessfully
    ) {
      return true;
    }
    return false;
  }

  get isPromoCodeAppliedSuccessfully(): boolean {
    if (
      (this.promoCodeCartObject && !this.promoCodeCartObject.valid) ||
      !this.promoCodeTotal
    ) {
      return false;
    }
    return true;
  }

  get addedToCartText(): string {
    return this.screenText.getScreenText("CATALOG_SESSION_ADDED_TO_CART");
  }

  get failedToAddSessionText(): string {
    return this.screenText.getScreenText(
      "CATALOG_SESSION_FAILED_ADDING_TO_CART"
    );
  }

  get userId(): number {
    const userDetail = commonEntryStore.userDetail as any;
    const coreIds = APP_UTILITIES.coreidsSync();

    const areUserDetailsAvailable = userDetail && !!userDetail.id;
    const isCookieValueAvailable = coreIds && !!coreIds.id;

    let userId = -1;

    if (areUserDetailsAvailable) {
      userId = parseInt(userDetail.id);
    } else if (isCookieValueAvailable) {
      userId = coreIds.id;
    }

    return userId;
  }

  get accountId(): number {
    const userDetail = commonEntryStore.userDetail as any;
    const coreIds = APP_UTILITIES.coreidsSync();

    const areUserDetailsAvailable =
      userDetail && !!userDetail.userRoles && userDetail.userRoles.length > 0;
    const isCookieValueAvailable = coreIds && !!coreIds.accountId;

    let accountId = -1;

    if (areUserDetailsAvailable) {
      accountId = (commonEntryStore.userDetail as any).userRoles[0].accountId;
    } else if (isCookieValueAvailable) {
      accountId = coreIds.accountId;
    }

    return accountId;
  }

  get isUserLoggedIn(): boolean {
    return this.userId > 0 && this.accountId > 0;
  }

  get cartApplicationCount(): number {
    return this.cartApplications
      ? this.cartApplications.cartItemApplicationData.length
      : 0;
  }

  get currentlySelectedCartApplication(): CartApplication | null {
    let result: CartApplication | null = null;
    if (this.cartApplications) {
      result =
        this.cartApplications.cartItemApplicationData[
          this.currentlySelectedApplicationIndex
        ] || null;
    }
    return result;
  }

  get invalidCartItems(): { session: CatalogSession; items: CartItem[] }[] {
    return this.cartItemsBySession
      .map(({ session, items }) => {
        if (session.sessionEnrollmentStatus == SessionEnrollmentStatus.Ended) {
          // all items with an ended session are invalid
          return {
            session,
            items,
          };
        } else {
          // if scholarCapacity is defined, there is a hard limit to the number of enrolled scholars
          if (
            session.scholarCapacity !== undefined &&
            !hasWaitlistIsUnlimitedActive(session)
          ) {
            const remaining =
              session.scholarCapacity +
              (session.waitlistCapacity || 0) -
              (session.enrollmentCount + session.waitlistCount);
            if (items.length > remaining) {
              return {
                session,
                items: items.slice(remaining),
              };
            }
          }
        }
      })
      .filter(
        (e): e is { session: CatalogSession; items: CartItem[] } =>
          e !== undefined
      );
  }

  get didAssociateScholarFail(): boolean {
    return this.associateScholarFailedError !== null;
  }

  @Action
  async setCart(setCartPayload: SetCartPayload) {
    this.VALIDATE_AND_SET_CART(setCartPayload.cartResponse);
    if (setCartPayload.shouldResetApplications) {
      this.RESET_CART_APPLICATIONS();
    }
  }

  @Mutation
  [VALIDATE_AND_SET_CART](cartResponse: GetCartResponse) {
    // Filter out items with null sessions
    this.activeCart = {
      ...cartResponse,
      cartItems: cartResponse.cartItems
        .filter((item) => item.session !== undefined)
        .map((item) => ({
          cartItemId: item.cartItemId,
          scholar: item.scholar,
          session: item.session!,
        })),
    };
    // If not every item has a defined session, then some items were removed
    this.invalidItemsRemoved = !cartResponse.cartItems.every(
      (item) => item.session
    );

    // Apply promo code
    if (this.promoCode) {
      this.isPromoCodeApplied = true;
      if (this.activeCart.promoCodeValidation) {
        this.hasPromoCodeError = !this.activeCart.promoCodeValidation.valid;
        this.promoCodeErrorMessage = this.activeCart.promoCodeValidation.error;
      }
    } else {
      this.isPromoCodeApplied = false;
    }
  }

  // Set selected payment option between online payment (default option) and custom payment (if custom payment is available)
  @Action
  async setSelectedPaymentOption(selected: PaymentOptions) {
    this.SET_SELECTED_PAYMENT_OPTION(selected);
  }

  @Mutation
  [SET_SELECTED_PAYMENT_OPTION](selected: PaymentOptions) {
    this.selectedPaymentOption = selected;
  }

  // Set current custom checkout option if available for the account
  @Action
  async setCustomCheckout(option: CustomCheckoutOption) {
    this.SET_CUSTOM_CHECKOUT(option);
  }

  @Mutation
  [SET_CUSTOM_CHECKOUT](option: CustomCheckoutOption) {
    this.customCheckoutOption = option;
  }

  @Mutation
  [FETCH_CUSTOM_CHECKOUT_REQUESTED]() {
    this.isFetchingCustomCheckout = true;
    this.didFetchCustomCheckoutFail = false;
    this.fetchCustomCheckoutFailedError = null;
  }

  @Mutation
  [FETCH_CUSTOM_CHECKOUT_SUCCEEDED]() {
    this.isFetchingCustomCheckout = false;
    this.didFetchCustomCheckoutFail = false;
    this.fetchCustomCheckoutFailedError = null;
  }

  @Mutation
  [FETCH_CUSTOM_CHECKOUT_FAILED](error: ApiErrorResponse) {
    this.isFetchingCustomCheckout = false;
    this.didFetchCustomCheckoutFail = true;
    this.fetchCustomCheckoutFailedError = error;
  }

  @Mutation
  [FETCH_CART_REQUESTED]() {
    this.isFetchingCart = true;
    this.didFetchCartFail = false;
    this.fetchCartFailedError = null;
  }

  @Mutation
  [FETCH_CART_SUCCEEDED]() {
    this.isFetchingCart = false;
    this.didFetchCartFail = false;
    this.fetchCartFailedError = null;
  }

  @Mutation
  [FETCH_CART_FAILED](error: ApiErrorResponse) {
    this.isFetchingCart = false;
    this.didFetchCartFail = true;
    this.fetchCartFailedError = error;
  }

  @Mutation
  [RESET_CART]() {
    this.activeCart = {
      userId: 0,
      cartId: 0,
      totals: {
        subtotal: 0,
        tax: 0,
        promoCodeDiscount: 0,
        discountPercentage: 0,
        total: 0
      },
      promoCodeValidation: null,
      cartItems: [],
    };
    this.isFetchingCart = false;
    this.didFetchCartFail = false;
    this.fetchCartFailedError = null;

    this.isAddingToCart = false;
    this.didCartAddFail = false;
    this.cartAddFailedError = null;

    this.isUpdatingCart = false;
    this.didCartUpdateFail = false;
    this.cartUpdateFailedError = null;

    this.invalidItemsRemoved = false;
  }

  @Mutation
  [ADD_CARTITEM_REQUESTED](sessionId: number) {
    this.isAddingToCart = true;
    this.didCartAddFail = false;
    this.cartAddFailedError = null;
    this.currentInteractionSessionId = sessionId;
  }

  @Mutation
  [ADD_CARTITEM_SUCCEEDED]() {
    this.isAddingToCart = false;
    this.didCartAddFail = false;
    this.cartAddFailedError = null;
    this.currentInteractionSessionId = null;
  }

  @Mutation
  [ADD_CARTITEM_FAILED](error: ApiErrorResponse) {
    this.isAddingToCart = false;
    this.didCartAddFail = true;
    this.cartAddFailedError = error;
    this.currentInteractionSessionId = null;
  }

  @Mutation
  [UPDATE_CARTITEM_REQUESTED]() {
    this.isUpdatingCart = true;
    this.didCartUpdateFail = false;
    this.cartUpdateFailedError = null;
  }

  @Mutation
  [UPDATE_CARTITEM_SUCCEEDED]() {
    this.isUpdatingCart = false;
    this.didCartUpdateFail = false;
    this.cartUpdateFailedError = null;
  }

  @Mutation
  [UPDATE_CARTITEM_FAILED](error: ApiErrorResponse) {
    this.isUpdatingCart = false;
    this.didCartUpdateFail = true;
    this.cartUpdateFailedError = error;
  }

  @Mutation
  [INCREMENT_CURRENTLY_SELECTED_APPLICATION]() {
    this.currentlySelectedApplicationIndex++;
  }

  @Mutation
  [DECREMENT_CURRENTLY_SELECTED_APPLICATION]() {
    this.currentlySelectedApplicationIndex--;
  }

  @Mutation
  [APPLICATIONS_COMPLETE]() {
    this.applicationsComplete = true;
  }

  @Mutation
  [RESET_APPLICATIONS_COMPLETE]() {
    this.applicationsComplete = false;
  }

  @Mutation
  [DELETE_CARTITEM_REQUESTED](sessionId?: number) {
    this.isDeleting = true;
    this.didCartDeleteFail = false;
    this.cartDeleteFailedError = null;
    if (sessionId) {
      this.currentInteractionSessionId = sessionId;
    }
  }

  @Mutation
  [DELETE_CARTITEM_SUCCEEDED]() {
    this.isDeleting = false;
    this.didCartDeleteFail = false;
    this.cartDeleteFailedError = null;
    this.currentInteractionSessionId = null;
  }

  @Mutation
  [DELETE_CARTITEM_FAILED](error: ApiErrorResponse) {
    this.isDeleting = false;
    this.didCartDeleteFail = true;
    this.cartDeleteFailedError = error;
    this.currentInteractionSessionId = null;
  }

  @Mutation
  [IS_INITIALIZING_CART_REQUESTED]() {
    this.isInitializingCart = true;
  }

  @Mutation
  [IS_INITIALIZING_CART_COMPLETED]() {
    this.isInitializingCart = false;
  }

  @Mutation
  [FETCH_CART_APPLICATIONS_REQUESTED]() {
    this.isFetchingCartApplications = true;
    this.didFetchCartApplicationsFail = false;
    this.fetchCartApplicationsFailedError = null;
  }

  @Mutation
  [FETCH_CART_APPLICATIONS_SUCCEEDED](
    applicationsForCart: GetCartApplicationsResponse
  ) {
    // Associate applications with their respective cart items
    const cartItemsById = Object.fromEntries(
      this.activeCart.cartItems.map((item) => [item.cartItemId, item])
    );
    applicationsForCart.cartItemApplicationData.forEach(
      (application) =>
        (application.cartItem = cartItemsById[application.cartItemId])
    );

    this.cartApplications = applicationsForCart;
    this.isFetchingCartApplications = false;
    this.didFetchCartApplicationsFail = false;
    this.fetchCartApplicationsFailedError = null;
  }

  @Mutation
  [FETCH_CART_APPLICATIONS_FAILED](error: ApiErrorResponse) {
    this.isFetchingCartApplications = false;
    this.didFetchCartApplicationsFail = true;
    this.fetchCartApplicationsFailedError = error;
  }

  @Mutation
  [RESET_CART_APPLICATIONS]() {
    this.cartApplications = null;
    this.isFetchingCartApplications = false;
    this.didFetchCartApplicationsFail = false;
    this.fetchCartApplicationsFailedError = null;
    this.currentlySelectedApplicationIndex = 0;
  }

  @Mutation
  [SET_IS_LOADING_CART_APPLICATIONS](value: boolean) {
    this.isLoadingCartApplicationsData = value;
  }

  @Mutation
  [COPY_ACTIVE_CART_TO_SUBMITTED_CART]() {
    this.submittedCart = { ...this.activeCart };
  }

  @Mutation
  [RESET_SUBMITTED_CART]() {
    this.submittedCart = null;
  }

  @Mutation
  [FETCH_STORE_POLICY_REQUESTED]() {
    this.isFetchingStorePolicy = true;
    this.didFetchStorePolicyFail = false;
    this.fetchStorePolicyFailedError = null;
  }

  @Mutation
  [FETCH_STORE_POLICY_SUCCEEDED](storePolicy: StorePolicy) {
    this.isFetchingStorePolicy = false;
    this.didFetchStorePolicyFail = false;
    this.fetchStorePolicyFailedError = null;
    this.storePolicy = storePolicy;
  }

  @Mutation
  [FETCH_STORE_POLICY_FAILED](error: ApiErrorResponse) {
    this.isFetchingStorePolicy = false;
    this.didFetchStorePolicyFail = true;
    this.fetchStorePolicyFailedError = error;
    this.storePolicy = { enabled: false, url: "" };
  }

  @Mutation
  [ASSOCIATE_SCHOLAR_REQUESTED]() {
    this.isAssociatingScholar = true;
    this.associateScholarFailedError = null;
  }

  @Mutation
  [ASSOCIATE_SCHOLAR_SUCCEEDED]() {
    this.isAssociatingScholar = false;
    this.associateScholarFailedError = null;
  }

  @Mutation
  [ASSOCIATE_SCHOLAR_FAILED](error: ApiErrorResponse) {
    this.isAssociatingScholar = false;
    this.associateScholarFailedError = error;
  }

  @Mutation
  [RESET_STORE_POLICY]() {
    this.storePolicy = {
      enabled: false,
      url: "",
    };
  }

  @Mutation
  [SET_CURRENTLY_SELECTED_APPLICATION_INDEX](index: number) {
    this.currentlySelectedApplicationIndex = index;
  }

  @Mutation [SET_PROMO_CODE](code: string) {
    this.promoCode = code;
    this.hasPromoCodeError = false;
    this.promoCodeErrorMessage = "";
    if (code === "") {
      this.isPromoCodeApplied = false;
    }
  }

  @Mutation
  [SET_FETCHING_CART_FOR_PROMO_CODE](val: boolean) {
    this.isFetchingCartForPromoCode = val;
  }

  @Action
  async removeSpecificCartItem(cartItem: CartItem) {
    if (!this.isUserLoggedIn) {
      const index = this.activeCart.cartItems.findIndex(
        (item) =>
          item.cartItemId === cartItem.cartItemId &&
          item.session === cartItem.session &&
          item.scholar === cartItem.scholar
      );
      if (index != -1) {
        this.activeCart.cartItems.splice(index, 1);
      }
      CartLocalStorageUtilities.setLocalStorageCart(this.activeCart);
    } else {
      try {
        this.DELETE_CARTITEM_REQUESTED();

        const removeCartItemRequest = {
          cartItemId: cartItem.cartItemId,
        } as RemoveCartItemRequest;
        await removeCartItem(removeCartItemRequest);

        const response = await getCartForUser(
          this.userId,
          this.accountId,
          this.applicationsComplete,
          this.promoCode
        );
        this.setCart({
          cartResponse: response.data,
          shouldResetApplications: true,
        } as SetCartPayload);
        this.DELETE_CARTITEM_SUCCEEDED();
      } catch (error) {
        const apiErrorResponse: ApiErrorResponse = APP_UTILITIES.errorToApiErrorResponse(
          error
        );
        this.DELETE_CARTITEM_FAILED(apiErrorResponse);
      }
    }
  }

  @Action
  async mergeLocalCartToServer(cart: Cart) {
    try {
      this.FETCH_CART_REQUESTED();
      const getCartresponse = await getCartForUser(this.userId, this.accountId);

      CartLocalStorageUtilities.removeLocalStorageCart();

      const combinedAddActionPayloads = cart.cartItems.reduce(
        (acc: Array<AddCartItemRequest>, curr) => {
          const foundValue = acc.find(
            (x) => x.sessionId === curr.session.sessionId
          );
          if (foundValue) {
            foundValue.quantity++;
          } else {
            const newAddPayload = {
              cartId: getCartresponse.data.cartId,
              sessionId: curr.session.sessionId,
              quantity: 1,
            } as AddCartItemRequest;
            acc.push(newAddPayload);
          }
          return acc;
        },
        []
      );

      await Promise.all(
        combinedAddActionPayloads.map((payload: AddCartItemRequest) =>
          addNewItem(payload)
        )
      );

      const updatedCartResponse = await getCartForUser(
        this.userId,
        this.accountId
      );
      this.setCart({
        cartResponse: updatedCartResponse.data,
        shouldResetApplications: true,
      } as SetCartPayload);
      await this.fetchCartApplications();
      if (this.cartApplications) {
        await applicationStore.updateApplicationData(
          this.cartApplications.rawApplications[
            this.currentlySelectedApplicationIndex
          ].elasticApplicationId
        );
      }
      this.FETCH_CART_SUCCEEDED();

      return updatedCartResponse.data;
    } catch (error) {
      const apiErrorResponse: ApiErrorResponse = APP_UTILITIES.errorToApiErrorResponse(
        error
      );
      this.FETCH_CART_FAILED(apiErrorResponse);
    }
  }

  @Action
  async initializeCartState(isAuthenticated: boolean) {
    this.IS_INITIALIZING_CART_REQUESTED();
    const localStorageCart = CartLocalStorageUtilities.getLocalStorageCart();
    if (localStorageCart) {
      this.setCart({
        cartResponse: localStorageCart,
        shouldResetApplications: true,
      } as SetCartPayload);
      this.FETCH_CART_SUCCEEDED();
    }

    if (isAuthenticated) {
      const isTransitioningToAuthenticated = !!localStorageCart;
      if (isTransitioningToAuthenticated) {
        await this.mergeLocalCartToServer(localStorageCart!);
      } else {
        await this.fetchCartForUser();
      }
    }
    this.IS_INITIALIZING_CART_COMPLETED();
  }

  @Action
  async fetchCustomCheckoutOptions(accountId: number) {
    try {
      this.FETCH_CUSTOM_CHECKOUT_REQUESTED();
      const response = await getCustomCheckoutOptionList(accountId);
      this.setCustomCheckout(response.data[0]);
      this.FETCH_CUSTOM_CHECKOUT_SUCCEEDED();
      return response.data;
    } catch (error) {
      const apiErrorResponse: ApiErrorResponse = APP_UTILITIES.errorToApiErrorResponse(
        error
      );
      this.FETCH_CUSTOM_CHECKOUT_FAILED(apiErrorResponse);
    }
  }

  @Action
  async fetchCartForUser(shouldResetApplications: boolean = true) {
    if (!this.isUserLoggedIn) {
      return this.activeCart;
    }
    try {
      this.FETCH_CART_REQUESTED();
      let response;
      if (this.applicationsComplete) {
        response = await getCartForUser(
          this.userId,
          this.accountId,
          this.applicationsComplete,
          this.total === 0 ? "" : this.promoCode
        );
      } else {
        response = await getCartForUser(this.userId, this.accountId);
      }

      this.setCart({
        cartResponse: response.data,
        shouldResetApplications,
      } as SetCartPayload);
      this.FETCH_CART_SUCCEEDED();
      return response.data;
    } catch (error) {
      if (!axios.isCancel(error)) {
        const apiErrorResponse: ApiErrorResponse = APP_UTILITIES.errorToApiErrorResponse(
          error
        );
        this.FETCH_CART_FAILED(apiErrorResponse);
      }
    }
  }

  @Action
  async addCartItem(addCartItemActionPayload: AddCartItemActionPayload) {
    if (this.isAddingToCart) {
      return;
    }
    const existingLocalStorageCart = CartLocalStorageUtilities.getLocalStorageCart();
    const addCartItemRequest = {
      cartId: this.activeCart.cartId,
      sessionId: addCartItemActionPayload.session.sessionId,
      quantity: addCartItemActionPayload.quantity,
    } as AddCartItemRequest;

    if (!this.isUserLoggedIn) {
      for (let i = 0; i < addCartItemActionPayload.quantity; i++) {
        const cartItemId = existingLocalStorageCart
          ? existingLocalStorageCart.cartItems.length + i
          : i;

        this.activeCart.cartItems.push({
          cartItemId: cartItemId,
          session: addCartItemActionPayload.session,
        } as CartItem);
      }
      CartLocalStorageUtilities.setLocalStorageCart(this.activeCart);
      const quantityAdded = addCartItemActionPayload.quantity;
      if (addCartItemActionPayload.shouldNotifyUser) {
        APP_UTILITIES.showToastMessage(
          `${quantityAdded} ${this.addedToCartText}`,
          ToastType.Success
        );
      }
    } else {
      try {
        this.ADD_CARTITEM_REQUESTED(addCartItemActionPayload.session.sessionId);

        await addNewItem(addCartItemRequest);
        const response = await getCartForUser(this.userId, this.accountId);
        this.setCart({
          cartResponse: response.data,
          shouldResetApplications: true,
        } as SetCartPayload);
        this.ADD_CARTITEM_SUCCEEDED();
        const quantityAdded = addCartItemActionPayload.quantity;
        if (addCartItemActionPayload.shouldNotifyUser) {
          APP_UTILITIES.showToastMessage(
            `${quantityAdded} ${this.addedToCartText}`,
            ToastType.Success
          );
        }
      } catch (error) {
        const apiErrorResponse: ApiErrorResponse = APP_UTILITIES.errorToApiErrorResponse(
          error
        );
        this.ADD_CARTITEM_FAILED(apiErrorResponse);

        if (!axios.isCancel(error)) {
          const sessionFailedTitle =
            addCartItemActionPayload.session.sessionName;
          APP_UTILITIES.showToastMessage(
            `${sessionFailedTitle} ${this.failedToAddSessionText}`,
            ToastType.Error
          );
        }
      }
    }
  }

  @Action
  async updateCartItem(
    updateCartItemActionPayload: UpdateCartItemActionPayload
  ) {
    if (!this.isUserLoggedIn) {
      const cartIds = this.activeCart.cartItems
        .filter(
          (cartItem) =>
            cartItem.session.sessionId ===
            updateCartItemActionPayload.session.sessionId
        )
        .map((x) => x.cartItemId);

      const shouldRemoveExcessSessionsInCart =
        cartIds.length >
        updateCartItemActionPayload.updateCartItemPayload.quantity;
      const shouldAddSessionsUpToQuantity =
        cartIds.length <
        updateCartItemActionPayload.updateCartItemPayload.quantity;

      if (shouldRemoveExcessSessionsInCart) {
        const cartItemsMatchingSession = this.activeCart.cartItems.filter(
          (item) =>
            item.session.sessionId ===
            updateCartItemActionPayload.session.sessionId
        );
        const cartItemsNotMatchingSession = this.activeCart.cartItems.filter(
          (item) =>
            item.session.sessionId !=
            updateCartItemActionPayload.session.sessionId
        );

        this.activeCart.cartItems = [
          ...cartItemsNotMatchingSession,
          ...cartItemsMatchingSession.splice(
            0,
            updateCartItemActionPayload.updateCartItemPayload.quantity
          ),
        ];
      } else if (shouldAddSessionsUpToQuantity) {
        this.addCartItem({
          session: updateCartItemActionPayload.session,
          userId: this.userId,
          accountId: this.accountId,
          quantity:
            updateCartItemActionPayload.updateCartItemPayload.quantity -
            cartIds.length,
          shouldNotifyUser: false,
        } as AddCartItemActionPayload);
      }
      CartLocalStorageUtilities.setLocalStorageCart(this.activeCart);
    } else {
      const isDeleting =
        updateCartItemActionPayload.updateCartItemPayload.quantity === 0;
      try {
        isDeleting
          ? this.DELETE_CARTITEM_REQUESTED(
              updateCartItemActionPayload.session.sessionId
            )
          : this.UPDATE_CARTITEM_REQUESTED();
        await updateItem(updateCartItemActionPayload.updateCartItemPayload);
        const response = await getCartForUser(
          this.userId,
          this.accountId,
          this.applicationsComplete,
          this.promoCode
        );
        this.setCart({
          cartResponse: response.data,
          shouldResetApplications: true,
        } as SetCartPayload);
        isDeleting
          ? this.DELETE_CARTITEM_SUCCEEDED()
          : this.UPDATE_CARTITEM_SUCCEEDED();
      } catch (error) {
        const apiErrorResponse: ApiErrorResponse = APP_UTILITIES.errorToApiErrorResponse(
          error
        );
        isDeleting
          ? this.DELETE_CARTITEM_FAILED(apiErrorResponse)
          : this.UPDATE_CARTITEM_FAILED(apiErrorResponse);
      }
    }
  }

  @Action
  async fetchCartApplications() {
    try {
      this.FETCH_CART_APPLICATIONS_REQUESTED();
      const response = await getCartApplications(this.userId, this.accountId);
      this.FETCH_CART_APPLICATIONS_SUCCEEDED(response.data);
      return response;
    } catch (error) {
      const apiErrorResponse: ApiErrorResponse = APP_UTILITIES.errorToApiErrorResponse(
        error
      );
      this.FETCH_CART_APPLICATIONS_FAILED(apiErrorResponse);
    }
  }

  @Action
  async loadCartApplicationData(config?: LoadCartApplicationsDataConfig) {
    const indexAction = config && config.indexAction;
    const resetFormScholarData =
      config && config.resetFormScholarData !== undefined
        ? config.resetFormScholarData
        : true;

    this.SET_IS_LOADING_CART_APPLICATIONS(true);
    APP_UTILITIES.removeSpecificCookie("applicationId");
    APP_UTILITIES.removeSpecificCookie("elasticEnrollmentId");
    const cartApplicationsResponse = await this.fetchCartApplications();
    if (cartApplicationsResponse && cartApplicationsResponse.status === 200) {
      switch (indexAction) {
        case IndexAction.Increment:
          this.INCREMENT_CURRENTLY_SELECTED_APPLICATION();
          break;
        case IndexAction.Decrement:
          this.DECREMENT_CURRENTLY_SELECTED_APPLICATION();
          break;
        case IndexAction.SeekEnd:
          this.SET_CURRENTLY_SELECTED_APPLICATION_INDEX(
            this.cartApplicationCount - 1
          );
          break;
      }

      if (this.currentlySelectedCartApplication) {
        const elasticApplicationId = this.currentlySelectedCartApplication
          .rawApplicationId; // This is the master/template Application ID
        APP_UTILITIES.setCookie("applicationId", elasticApplicationId, 1);

        const rawApplicationToUse = cartApplicationsResponse.data.rawApplications.filter(
          (e) => e.elasticApplicationId == elasticApplicationId
        )[0];

        const applicationData = rawApplicationToUse as IApplicationData;

        applicationStore.mutateApplicationData(applicationData);

        applicationStore.getDropDownScholars(this.userId);

        const elasticSubmissionId = this.currentlySelectedCartApplication
          .elasticSubmissionId;
        formsStore.resetApplication();
        if (resetFormScholarData) {
          formsStore.updateScholarData({});
        }
        formsStore.SET_ELASTIC_ENROLLMENT_ID("");
        if (elasticSubmissionId) {
          formsStore.SET_ELASTIC_ENROLLMENT_ID(elasticSubmissionId);
          APP_UTILITIES.setCookie(
            "elasticEnrollmentId",
            elasticSubmissionId,
            1
          );
        }
        const applicationInProgress =
          cartApplicationsResponse.data.cartItemApplicationData[
            this.currentlySelectedApplicationIndex
          ].applicationInProgress;
        if (applicationInProgress) {
          const doesApplicationCurrentlyExist =
            applicationInProgress.formsList.name;
          if (doesApplicationCurrentlyExist) {
            formsStore.getElasticApplications(applicationInProgress);
          } else {
            const newApplication = {
              formsList: rawApplicationToUse,
              siteIds: rawApplicationToUse.siteIds,
            };
            formsStore.getElasticApplications(newApplication);
          }
        }
      }
    }
    this.SET_IS_LOADING_CART_APPLICATIONS(false);
  }

  @Action
  configureCartAndApplicationsAfterSubmission() {
    this.COPY_ACTIVE_CART_TO_SUBMITTED_CART();
    this.RESET_CART();
    this.RESET_CART_APPLICATIONS();
    this.RESET_APPLICATIONS_COMPLETE();
    this.SET_PROMO_CODE("");
  }

  @Action
  async fetchStorePolicy() {
    try {
      const accountId = this.accountId;
      if (accountId > 0) {
        this.RESET_STORE_POLICY();
        this.FETCH_STORE_POLICY_REQUESTED();
        const response = await getTermsOfService(accountId);
        this.FETCH_STORE_POLICY_SUCCEEDED(response.data);
      }
    } catch (error) {
      const apiErrorResponse: ApiErrorResponse = APP_UTILITIES.errorToApiErrorResponse(
        error
      );
      this.FETCH_STORE_POLICY_FAILED(apiErrorResponse);
    }
  }

  @Action
  async associateScholarToCurrentlySelectedApplicationCartItem(
    scholar?: ScholarDropdown
  ) {
    if (this.currentlySelectedCartApplication) {
      try {
        let request: AssociateScholarToCartItemRequest = {
          cartItemId: this.currentlySelectedCartApplication!.cartItemId,
          guardianUserId: this.userId,
        };
        if (scholar) {
          request = {
            ...request,
            scholarGuid: scholar.id,
            scholarFirstName: scholar.firstName,
            scholarLastName: scholar.lastName,
            scholarDateOfBirth: scholar.DOB,
          };
        }
        this.ASSOCIATE_SCHOLAR_REQUESTED();
        const response = await associateScholar(request);
        if (response) {
          this.ASSOCIATE_SCHOLAR_SUCCEEDED();
          return response;
        } else {
          // On a 500 Internal Service Error, httpEnrollmentClient will fire the showErrorPopup event without rejecting the promise.
          // We add the error here manually to indicate that the `associateScholar` call failed.
          this.ASSOCIATE_SCHOLAR_FAILED({
            name: ApiErrorType.Error_Unknown,
            value: "Internal Server Error", // XXX should this be something else?
            resourceNotFound: false,
            searchedLocation: "",
          });
        }
      } catch (error) {
        const apiErrorResponse: ApiErrorResponse = APP_UTILITIES.errorToApiErrorResponse(
          error
        );
        this.ASSOCIATE_SCHOLAR_FAILED(apiErrorResponse);
        // httpEnrollmentClient has builtin handling for 500 and 401 errors.
        // For all other errors, we fire the showErrorPopup event manually.
          Vue.prototype.$eventHub.$emit('showErrorPopup', { name: 'Common_Error' });
      }
    }
  }
}

export default getModule(CartStoreModule);
